/**
 * WhatsAppOptIn — the booking widget's WhatsApp consent checkbox
 * (CD-Docket 2026-08-04 · voyage#426 item 6 · PR #524 Lane 6).
 *
 * WhatsApp is OPT-IN (docket rule 1): this renders UNCHECKED unless the server
 * reports a stored `true`, and it is the only way a customer can ever consent —
 * without it the backend suppresses every WhatsApp send with `consent_missing`.
 * Email/SMS keep their opt-OUT semantics and are not touched here.
 *
 * WIDGET HARD RULE: classes/ids only, no inline styles (see widget-consent.css).
 *
 * Writes go through the typed client ops only — getCustomerMessagingPreferences /
 * updateCustomerMessagingPreferences — with EXPLICIT booleans (docket rule 4:
 * never omit a key; the server persists explicit false).
 */
import { useEffect, useState } from "react";
import { getCustomerMessagingPreferences, updateCustomerMessagingPreferences } from "@v0y4ge/api-client";
import { ensureWidgetConsentCss } from "./widget-consent-styles";

export type ConsentTopic = "bookings" | "events" | "general";
export interface ChannelConsent { bookings?: boolean; events?: boolean; general?: boolean }
export interface ConsentPrefs { email?: ChannelConsent; sms?: ChannelConsent; whatsapp?: ChannelConsent }
export interface ConsentSuppression { channel: string; address?: string | null; reason?: string | null; code?: string | null; created_at?: string | null }
export interface ConsentRecord { customer_id?: string; preferences: ConsentPrefs; suppressions: ConsentSuppression[] }

const unwrap = (r: unknown): ConsentRecord | null => {
  const rec = r && typeof r === "object" && "data" in (r as Record<string, unknown>) ? (r as { data: unknown }).data : r;
  if (!rec || typeof rec !== "object") return null;
  const v = rec as Record<string, unknown>;
  return {
    customer_id: typeof v.customer_id === "string" ? v.customer_id : undefined,
    preferences: (v.preferences && typeof v.preferences === "object" ? v.preferences : {}) as ConsentPrefs,
    suppressions: Array.isArray(v.suppressions) ? v.suppressions as ConsentSuppression[] : [],
  };
};

export async function readConsent(customerId?: string): Promise<ConsentRecord | null> {
  try {
    return unwrap(await getCustomerMessagingPreferences(customerId ? { customer_id: customerId } : {}));
  } catch { return null; }
}

/** Opt-OUT channel (email/sms): a missing value means consent. */
export const optOutOn = (c: ChannelConsent | undefined, topic: ConsentTopic): boolean => (c ? c[topic] !== false : true);
/** Opt-IN channel (whatsapp): ONLY an explicit true counts. */
export const optInOn = (c: ChannelConsent | undefined, topic: ConsentTopic): boolean => c?.[topic] === true;

/** Every key explicit — docket rule 4. */
export const explicitOptOut = (c: ChannelConsent | undefined): ChannelConsent =>
  ({ bookings: optOutOn(c, "bookings"), events: optOutOn(c, "events"), general: optOutOn(c, "general") });
export const explicitOptIn = (c: ChannelConsent | undefined): ChannelConsent =>
  ({ bookings: optInOn(c, "bookings"), events: optInOn(c, "events"), general: optInOn(c, "general") });

/**
 * Persist the widget's WhatsApp booking opt-in. Reads the current record first so
 * the customer's other channels/topics are carried through untouched (a widget
 * must never silently reset an email opt-out); if the read is unavailable, only
 * the whatsapp block is written — still with all three keys explicit.
 */
export async function persistWhatsAppOptIn(optIn: boolean, customerId?: string): Promise<ConsentRecord | null> {
  const current = await readConsent(customerId);
  const wa = explicitOptIn(current?.preferences.whatsapp);
  const body: ConsentPrefs = { whatsapp: { ...wa, bookings: optIn } };
  if (current?.preferences.email) body.email = explicitOptOut(current.preferences.email);
  if (current?.preferences.sms) body.sms = explicitOptOut(current.preferences.sms);
  // Scope the write to the same record the read came from — an unscoped PUT would land
  // on whatever record the server resolves for an unscoped caller.
  return unwrap(await updateCustomerMessagingPreferences(body as never, customerId ? { customer_id: customerId } : {}));
}

/** True once the server reports a stored WhatsApp booking consent for this customer. */
export function useStoredWhatsAppOptIn(customerId?: string, enabled = true): boolean | null {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    if (!enabled) { setOn(null); return; }
    let alive = true;
    void readConsent(customerId).then((rec) => {
      if (!alive) return;
      // No record / unreachable ⇒ stay at "no consent" (opt-in never assumes yes).
      setOn(rec ? optInOn(rec.preferences.whatsapp, "bookings") : false);
    });
    return () => { alive = false; };
  }, [customerId, enabled]);
  return on;
}

/** The visitor's pending tick, kept OUTSIDE React (CD 2026-08-04, verifier finding 2).
 *  The submit handler must be able to read it even when the widget/preview has remounted
 *  between the tick and the confirm — an instance ref or component state resets on remount
 *  with no signal, which made the write a silent no-op. Session-scoped, one visitor. */
let pendingOptIn = false;
export const setPendingWhatsAppOptIn = (next: boolean): void => { pendingOptIn = next; };
export const pendingWhatsAppOptIn = (): boolean => pendingOptIn;

export interface WhatsAppOptInProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
  /** Rendered when the server already has this customer's consent on file. */
  storedNote?: string;
  disabled?: boolean;
}

export function WhatsAppOptIn({ checked, onChange, label, hint, storedNote, disabled }: WhatsAppOptInProps) {
  ensureWidgetConsentCss();
  return (
    <div className="jrni-consent-optin jrni-consent-optin--whatsapp" id="jrni-consent-whatsapp">
      <label className="jrni-consent-optin__row">
        {/* ui-raw: booking widget renders classes/ids only, no inline-style jrni-ui components (WIDGET HARD RULE) */}
        <input
          className="jrni-consent-optin__checkbox"
          id="jrni-consent-whatsapp-checkbox"
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => { setPendingWhatsAppOptIn(e.target.checked); onChange(e.target.checked); }}
        />
        <span className="jrni-consent-optin__text">
          {label}
          {hint ? <span className="jrni-consent-optin__hint">{hint}</span> : null}
          {storedNote ? <span className="jrni-consent-optin__state">{storedNote}</span> : null}
        </span>
      </label>
    </div>
  );
}
